import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Wallet, BookOpen, Sigma, ShieldCheck, Scale, Plus, UserCheck, CheckCircle2, XCircle, Clock } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="axiom-card p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <Icon className={`h-6 w-6 ${accent}`} />
      <p className="mt-3 font-display text-3xl text-slate-900">{value}</p>
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [myProblems, setMyProblems] = useState([]);
  const [mySubs, setMySubs] = useState([]);
  const [contributors, setContributors] = useState([]);

  const loadContributors = () => api.get("/admin/contributors").then((r) => setContributors(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    if (user?.role === "customer") api.get("/solve/my/problems").then((r) => setMyProblems(r.data)).catch(() => {});
    if (user?.role === "contributor") api.get("/solve/my/submissions").then((r) => setMySubs(r.data)).catch(() => {});
    if (user?.role === "admin") loadContributors();
  }, [user]);

  const toggleQualify = async (c) => {
    try {
      await api.post(`/admin/contributors/${c.id}/qualify`, { qualified: !c.qualified });
      toast.success(c.qualified ? `Revoked qualification for ${c.name}` : `Qualified ${c.name}`);
      loadContributors();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  if (!user || !stats) return <div className="px-6 py-20 text-center text-slate-500">Loading…</div>;

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8" data-testid="dashboard-page">
      <p className="eyebrow mb-2 capitalize">{user.role} workspace</p>
      <h1 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl">Welcome, {user.name}</h1>

      {user.role === "contributor" && (
        <div className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold ${user.qualified ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="qualification-badge">
          {user.qualified ? <><CheckCircle2 className="h-4 w-4" /> Qualified contributor</> : <><Clock className="h-4 w-4" /> Qualification pending — an administrator will review your account</>}
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat icon={Wallet} label="Ledger balance" value={`$${stats.balance?.toLocaleString()}`} accent="text-[#D4AF37]" />
        <Stat icon={BookOpen} label="Lessons done" value={`${stats.lessons_completed}/${stats.lessons_total}`} accent="text-cyan-700" />
        {"my_problems" in stats && <Stat icon={Sigma} label="My commissions" value={stats.my_problems} accent="text-[#E61A27]" />}
        {"awarded" in stats && <Stat icon={ShieldCheck} label="Awarded" value={stats.awarded} accent="text-emerald-700" />}
        {"my_submissions" in stats && <Stat icon={Sigma} label="My submissions" value={stats.my_submissions} accent="text-[#E61A27]" />}
        {"accepted" in stats && <Stat icon={ShieldCheck} label="Accepted" value={stats.accepted} accent="text-emerald-700" />}
        {"review_queue" in stats && <Stat icon={ShieldCheck} label="Review queue" value={stats.review_queue} accent="text-cyan-700" />}
        {"open_cases" in stats && <Stat icon={Scale} label="Open cases" value={stats.open_cases} accent="text-rose-700" />}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/learn" className="btn-ghost">Open the library</Link>
        <Link to="/solve" className="btn-ghost">Browse commissions</Link>
        {user.role === "customer" && <Link to="/solve/new" className="btn-crimson"><Plus className="h-4 w-4" /> Commission a problem</Link>}
        {["reviewer", "adjudicator"].includes(user.role) && <Link to="/verify" className="btn-crimson">Review queue</Link>}
        <Link to="/ledger" className="btn-ghost">Earnings ledger</Link>
      </div>

      {user.role === "customer" && (
        <div className="mt-10">
          <p className="eyebrow mb-3">My commissions</p>
          <div className="space-y-3">
            {myProblems.map((p) => (
              <Link to={`/solve/${p.id}`} key={p.id} data-testid={`my-problem-${p.id}`} className="axiom-card flex items-center justify-between p-4">
                <div><p className="text-sm text-slate-900">{p.title}</p><p className="text-xs text-slate-500">{p.submission_count} submissions · ${p.prize_amount?.toLocaleString()}</p></div>
                <StatusBadge status={p.status} />
              </Link>
            ))}
            {myProblems.length === 0 && <p className="text-sm text-slate-500">No commissions yet.</p>}
          </div>
        </div>
      )}

      {user.role === "contributor" && (
        <div className="mt-10">
          <p className="eyebrow mb-3">My submissions</p>
          <div className="space-y-3">
            {mySubs.map((s) => (
              <Link to={`/submissions/${s.id}`} key={s.id} data-testid={`my-submission-${s.id}`} className="axiom-card flex items-center justify-between p-4">
                <div><p className="text-sm text-slate-900">{s.problem_title}</p><p className="text-xs text-slate-500">v{s.version} · {s.reviews?.length || 0} reviews</p></div>
                <StatusBadge status={s.status} />
              </Link>
            ))}
            {mySubs.length === 0 && <p className="text-sm text-slate-500">No submissions yet — browse the marketplace.</p>}
          </div>
        </div>
      )}

      {user.role === "admin" && (
        <div className="mt-10" data-testid="admin-qualification-panel">
          <p className="eyebrow mb-3 flex items-center gap-2"><UserCheck className="h-4 w-4 text-[#E61A27]" /> Contributor qualification</p>
          <div className="space-y-3">
            {contributors.map((c) => (
              <div key={c.id} data-testid={`contributor-${c.id}`} className="axiom-card flex items-center justify-between p-4">
                <div>
                  <p className="text-sm text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold ${c.qualified ? "text-emerald-700" : "text-amber-700"}`}>
                    {c.qualified ? <><CheckCircle2 className="h-4 w-4" /> Qualified</> : <><Clock className="h-4 w-4" /> Pending</>}
                  </span>
                  <button
                    data-testid={`qualify-toggle-${c.id}`}
                    onClick={() => toggleQualify(c)}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${c.qualified ? "bg-rose-50 text-rose-700 hover:bg-rose-100" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>
                    {c.qualified ? <><XCircle className="h-4 w-4" /> Revoke</> : <><CheckCircle2 className="h-4 w-4" /> Qualify</>}
                  </button>
                </div>
              </div>
            ))}
            {contributors.length === 0 && <p className="text-sm text-slate-500">No contributor accounts yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
